import type { FinancialAccount, FundingInstrument } from "@payezy/api-client/public";
import { getAccountStatementOptions, listAccountsOptions, listFundingInstrumentsOptions, simulateDepositMutation } from "@payezy/api-client/public/query";
import { Alert, Badge, Button, Card, CardBody, CardHeader, DescriptionList, EmptyState, Field, Input, PageHeader, Select, Skeleton, Stat, Table, TD, TH, THead, TR, formatDate, formatUSD, toast } from "@payezy/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Copy, FileCheck2, FlaskConical, Landmark } from "lucide-react";
import { useState } from "react";
import { problemMessage } from "../lib/api";
import { useOrg } from "../lib/org";
import { DEV_TOOLS } from "../lib/payments";

function copy(text: string) {
  void navigator.clipboard.writeText(text).then(() => toast.success("Copied"));
}

function CopyValue({ value }: { value: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="font-mono">{value}</span>
      <button type="button" className="cursor-pointer text-ink-400 hover:text-ink-700" onClick={() => copy(value)} aria-label="Copy">
        <Copy className="size-3.5" />
      </button>
    </span>
  );
}

function FundingCard({ fi }: { fi: FundingInstrument }) {
  if (fi.kind === "virtual_account") {
    if (fi.status !== "active" || !fi.bank) {
      return (
        <Card>
          <CardHeader title="Bank deposits" />
          <CardBody className="text-sm text-ink-500">Your account number is being set up. This usually takes a minute.</CardBody>
        </Card>
      );
    }
    const b = fi.bank;
    return (
      <Card>
        <CardHeader title="Bank deposits" description={`Send ${fi.rails.map((r) => r.toUpperCase()).join(", ")} in USD from any US bank.`} />
        <CardBody>
          <DescriptionList
            items={[
              { label: "Beneficiary", value: b.beneficiary_name },
              { label: "Routing number", value: <CopyValue value={b.routing_number} /> },
              { label: "Account number", value: <CopyValue value={b.account_number} /> },
              { label: "Bank", value: [b.bank_name, b.bank_address].filter(Boolean).join(", ") },
              ...(b.memo ? [{ label: "Memo / reference", value: <CopyValue value={b.memo} /> }] : []),
            ]}
          />
        </CardBody>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader title="Stablecoin deposits" description={`Send ${fi.asset} or USDC on ${fi.network.replace(/_/g, " ")} only. Other networks or tokens can be lost.`} />
      <CardBody>
        {fi.address ? <DescriptionList items={[{ label: "Deposit address", value: <CopyValue value={fi.address} /> }]} /> : <span className="text-sm text-ink-500">Address pending</span>}
      </CardBody>
    </Card>
  );
}

function SimulateDepositCard({ account, onRequested }: { account: FinancialAccount; onRequested: () => void }) {
  const org = useOrg();
  const [amount, setAmount] = useState("250.00");
  const [rail, setRail] = useState<"ach" | "wire" | "fednow">("ach");
  const sim = useMutation({
    ...simulateDepositMutation(),
    onSuccess: () => {
      toast.success("Deposit requested. It will appear in a few seconds.");
      onRequested();
    },
    onError: (e) => toast.error(problemMessage(e)),
  });
  return (
    <Card>
      <CardHeader title={<span className="inline-flex items-center gap-2"><FlaskConical className="size-4 text-ink-500" />Sandbox</span>} description="Send test money to this account's bank details. Not available in production." />
      <CardBody>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            sim.mutate({ path: { orgId: org.org_id, accountId: account.id }, body: { amount, rail } });
          }}
        >
          <Field label="Amount (USD)" className="w-36"><Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" /></Field>
          <Field label="Rail" className="w-36">
            <Select value={rail} onChange={(e) => setRail(e.target.value as typeof rail)}>
              <option value="ach">ACH</option>
              <option value="wire">Wire</option>
              <option value="fednow">FedNow</option>
            </Select>
          </Field>
          <Button type="submit" variant="secondary" loading={sim.isPending}>Simulate deposit</Button>
        </form>
      </CardBody>
    </Card>
  );
}

function AccountView({ account }: { account: FinancialAccount }) {
  const org = useOrg();
  const qc = useQueryClient();
  const path = { orgId: org.org_id, accountId: account.id };
  const [watchUntil, setWatchUntil] = useState(0);
  const b = account.balance;
  const live = () => Date.now() < watchUntil || b.pending_in !== "0.00";
  const funding = useQuery(listFundingInstrumentsOptions({ path }));
  const statement = useQuery({ ...getAccountStatementOptions({ path, query: { limit: 10 } }), refetchInterval: () => (live() ? 2000 : false) });
  const watch = () => {
    setWatchUntil(Date.now() + 20_000);
    void qc.invalidateQueries();
  };
  return (
    <div className="grid gap-6">
      <Card>
        <CardBody className="grid gap-6 sm:grid-cols-3">
          <Stat label={`${account.name} · available`} value={formatUSD(b.available)} />
          <Stat label="Incoming" value={formatUSD(b.pending_in)} hint="Deposits that are still settling" />
          <Stat label="Outgoing" value={formatUSD(b.pending_out)} hint="Payments in progress" />
        </CardBody>
      </Card>
      <div className="grid gap-6 lg:grid-cols-2">
        {funding.isPending && <Skeleton className="h-40" />}
        {funding.data?.items.map((fi) => <FundingCard key={fi.id} fi={fi} />)}
      </div>
      {DEV_TOOLS && funding.data?.items.some((fi) => fi.kind === "virtual_account" && fi.status === "active") && <SimulateDepositCard account={account} onRequested={watch} />}
      <Card>
        <CardHeader
          title="Recent activity"
          actions={<Link to={"/o/$orgId/payments" as "/o/$orgId"} params={{ orgId: org.org_id }} className="text-sm font-medium text-brand-600 hover:underline">All payments</Link>}
        />
        {statement.data && statement.data.items.length > 0 ? (
          <Table>
            <THead><TR><TH>Date</TH><TH>Description</TH><TH>Status</TH><TH className="text-right">Amount</TH></TR></THead>
            <tbody>
              {statement.data.items.map((l) => (
                <TR key={l.id}>
                  <TD>{formatDate(l.effective_at)}</TD>
                  <TD>{l.description}</TD>
                  <TD><Badge tone={l.status === "posted" ? "success" : l.status === "pending" ? "info" : "neutral"}>{l.status}</Badge></TD>
                  <TD className={`text-right font-mono ${l.direction === "credit" ? "text-emerald-700" : ""}`}>
                    {l.direction === "credit" ? "+" : "−"}{formatUSD(l.amount)}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        ) : (
          <CardBody className="text-sm text-ink-500">{statement.isPending ? "Loading…" : "No transactions yet. Fund your account using the details above."}</CardBody>
        )}
      </Card>
    </div>
  );
}

export function OverviewPage() {
  const org = useOrg();
  const accounts = useQuery({
    ...listAccountsOptions({ path: { orgId: org.org_id } }),
    enabled: org.permissions.includes("accounts:view"),
    refetchInterval: (q) => (q.state.data?.items.some((a) => a.balance.pending_in !== "0.00") ? 2000 : false),
  });
  const primary = accounts.data?.items[0];
  const needsVerification = org.org_status === "kyc_required" || org.org_status === "kyc_pending";

  return (
    <>
      <PageHeader title={org.org_name} description="Balances and recent activity" />
      {accounts.isError && <Alert tone="danger">{problemMessage(accounts.error)}</Alert>}
      {primary ? (
        <AccountView account={primary} />
      ) : accounts.isPending ? (
        <Skeleton className="h-40" />
      ) : (
        <Card>
          <EmptyState
            icon={needsVerification ? <FileCheck2 className="size-6" /> : <Landmark className="size-6" />}
            title={org.org_status === "kyc_pending" ? "Verification in progress" : needsVerification ? "Verify to open your account" : "No accounts yet"}
            description={
              org.org_status === "kyc_pending"
                ? "We'll set up your account and deposit details as soon as verification completes."
                : needsVerification
                  ? "It takes about five minutes. You'll get US bank details and a stablecoin deposit address."
                  : "Your account is being set up."
            }
            action={
              needsVerification && org.permissions.includes("onboarding:manage") ? (
                <Link to="/o/$orgId/verification" params={{ orgId: org.org_id }}>
                  <Button>{org.org_status === "kyc_pending" ? "View status" : "Start verification"}</Button>
                </Link>
              ) : undefined
            }
          />
        </Card>
      )}
    </>
  );
}
